import type { PageGuide } from "../page-guide.js";

/**
 * Help guide for the Ideation page. Anchor ids here must match the
 * `data-help-anchor` attributes on the corresponding panels in
 * `routes/_authenticated/ideation.page.tsx`.
 */
export const ideationGuide: PageGuide = {
  route: "/ideation",
  version: 1,
  overviewArticleId: "ideation-overview",
  steps: [
    {
      id: "capture",
      title: "Start by capturing a thought",
      body: "Ideation builds on your notes. Capture an idea here — use [[wikilinks]] for named concepts or client work you want connected, and tags to theme it.",
      anchorId: "capture",
    },
    {
      id: "graph",
      title: "Your thinking becomes a graph",
      body: "Notes, the concepts in their tags, their authors, and linked work appear as a connected graph. Drag to pan, scroll to zoom, and use **Fit** to frame everything. Click a node to inspect it.",
      anchorId: "graph",
    },
    {
      id: "search",
      title: "Focus on what matters",
      body: "Search or filter by tag to narrow the graph to a theme or client, then save that view as a board to return to it.",
      anchorId: "search",
    },
    {
      id: "clusters",
      title: "Let Claude group related ideas",
      body: "Generate draft clusters to have related notes grouped into themes you can review, edit, and approve.",
      anchorId: "clusters",
    },
    {
      id: "decision",
      title: "Hand off to real work",
      body: "Turn a node or approved cluster into a decision log, a client portal brief, or a project discovery brief — keeping the source trail intact.",
      anchorId: "decision",
    },
  ],
  panels: [
    {
      anchorId: "capture",
      title: "Capture thought",
      body: "Quickly record an idea as a note. Use [[wikilinks]] to connect named ideas and tags to theme them — both feed the graph.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "boards",
      title: "Saved boards",
      body: "Save the current search/tag scope (and pinned node) as a board so you can return to a line of thinking later, or share it with colleagues.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "clusters",
      title: "Assisted clusters",
      body: "Claude groups related notes into draft clusters. Review, edit, and approve them; approved clusters can be published to a client portal brief.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "search",
      title: "Search & filter",
      body: "Narrow the graph to a theme or client by search term or tag. Reset clears the scope.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "graph",
      title: "Knowledge graph",
      body: "Nodes are notes, concepts, authors, and linked work; edges are wikilinks, mentions, tags, and authorship. Drag/scroll to navigate, click to select.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "claude-graph",
      title: "Claude graph",
      body: "Ask Claude questions about the current graph or selected node. Capture the answer as a note or post it to a team channel.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "prompts",
      title: "Idea brief",
      body: "Dominant themes and suggested prompts surfaced from the notes currently in scope.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "inspector",
      title: "Node inspector",
      body: "Details and connections for the selected node — its neighbours, tags, and links into CRM/project work.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "decision",
      title: "Decision hand-off",
      body: "Capture a decision from the selected node, recording the rationale and the source trail.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "portal-brief",
      title: "Portal brief hand-off",
      body: "Stage the selected node's thinking into a client portal brief section for review and publishing.",
      relatedArticleId: "ideation-overview",
    },
    {
      anchorId: "project-discovery",
      title: "Project discovery hand-off",
      body: "Turn the selected node into a project discovery brief to kick off delivery work.",
      relatedArticleId: "ideation-overview",
    },
  ],
  guidedActions: [
    {
      id: "capture-first-idea",
      label: "Capture your first idea",
      description: "The graph is empty — capture a note to get started.",
      when: (ctx) => (ctx.counts.notes ?? 0) === 0,
      actionAnchorId: "capture",
    },
    {
      id: "generate-clusters",
      label: "Generate draft clusters",
      description: "You have enough notes to group into themes Claude can draft for you.",
      when: (ctx) => (ctx.counts.notes ?? 0) >= 8 && (ctx.counts.clusters ?? 0) === 0,
      actionAnchorId: "clusters",
      requiresRole: ["admin", "exec", "sales"],
    },
    {
      id: "save-board",
      label: "Save this view as a board",
      description: "You've filtered the graph — save the scope so you can return to it.",
      when: (ctx) => (ctx.counts.boards ?? 0) === 0 && Boolean(ctx.flags.hasActiveScope),
      actionAnchorId: "boards",
    },
    {
      id: "hand-off-node",
      label: "Hand off the selected node",
      description: "Turn the approved thinking into a decision, portal brief, or project.",
      when: (ctx) => Boolean(ctx.flags.hasApprovedCluster) && Boolean(ctx.flags.hasSelectedNode),
      actionAnchorId: "decision",
    },
  ],
};
